// Modul pro správu výroby (Technologické a průvodní listy)
// - Seznam aktivních zakázek ve výrobním procesu
// - Generování a tisk Technologických listů (A4 Job Tickets)
// - Generování a tisk Průvodních listů (A4 Accompanying Sheets pro dílnu)
// - Generování a tisk DPD přepravních štítků (mockup)

import { rozsahProVyrobu } from './jednotky.js';
import { icon } from './icons.js';
import {
  API_BASE_URL, ListView, renderListCard, renderErrorState,
  escapeHtml, formatDateTime, customerNameOf, customerDataText,
  STATUS_META as SHARED_STATUS_META, statusBadge
} from './shared/list-ui.js';

// Výrobní pohled sdílí stavový model objednávek, jen s terminologií dílny.
const STATUS_META = SHARED_STATUS_META.vyroba;
const VYROBA_STATUS_ORDER = ['received', 'in_progress', 'done', 'to_invoice', 'invoiced', 'delivered', 'cancelled'];

export class VyrobaManager {
  constructor() {
    this.orders = [];
    this.loading = false;
    this.view = new ListView({
      key: 'vyroba',
      title: 'Řízení výroby',
      subtitle: 'Technologické a průvodní listy zakázek pro ofsetový a digitální tisk.',
      searchPlaceholder: 'Hledat (číslo, firma, položka)…',
      statusKind: 'vyroba',
      statuses: VYROBA_STATUS_ORDER,
      authorLabels: { me: 'Moje zakázky' },
      enableCustomerFilter: true,
      enableKanban: true,
      onChange: () => this.load()
    });
  }

  // ---------- API ----------

  async fetchOrders(params) {
    this.loading = true;
    const qs = params && params.toString() ? `?${params}` : '';
    const res = await fetch(`${API_BASE_URL}/orders${qs}`, { credentials: 'include' });
    if (!res.ok) {
      this.loading = false;
      throw new Error('Nepodařilo se načíst zakázky pro výrobu');
    }
    const json = await res.json();
    this.loading = false;
    return json;
  }

  async getOrder(id) {
    const res = await fetch(`${API_BASE_URL}/orders/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst detaily zakázky');
    const json = await res.json();
    return json.data;
  }

  async setOrderStatus(id, status, note = '') {
    const res = await fetch(`${API_BASE_URL}/orders/${id}/status`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, note })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se změnit stav zakázky');
    return json.data;
  }

  // ---------- Renderování sekce ----------

  async showSection() {
    await this.load();
  }

  async load() {
    const container = document.getElementById('vyroba-section-content');
    if (!container) return;
    if (!document.getElementById('vyroba-list')) {
      this.view.renderShell(container);
      this._bindListEvents();
    } else {
      this.view.renderLoading();
    }

    try {
      if (this.view.viewMode === 'kanban') {
        await this.loadKanban();
        return;
      }
      const result = await this.fetchOrders(this.view.buildParams());
      this.orders = result.data || [];
      if (Array.isArray(result.statuses) && result.statuses.length) this.view.statuses = result.statuses;
      if (Array.isArray(result.authors)) this.view.authors = result.authors;

      this.view.renderResults({
        rowsHtml: this.orders.map((o) => this.renderOrderCard(o)),
        total: result.total,
        emptyState: {
          title: 'Žádné zakázky ve výrobě',
          hint: 'Zakázky vznikají převodem schválené nabídky na objednávku.'
        }
      });
    } catch (err) {
      console.error('Chyba při načítání výroby:', err);
      this.view.renderCustom(renderErrorState(err.message || 'Nepodařilo se načíst zakázky', 'window.vyrobaManager.load()'));
    }
  }

  async loadKanban() {
    const { renderKanbanBoard } = await import('./shared/kanban.js');
    await renderKanbanBoard({
      view: this.view,
      statusKind: 'vyroba',
      fetchColumn: async (status) => {
        const params = this.view.buildParams({ paginate: false });
        params.set('status', status);
        params.set('limit', '30');
        params.set('sort', 'kanban');
        const result = await this.fetchOrders(params);
        return { items: result.data || [], total: result.total || 0 };
      },
      cardData: (o) => ({
        id: o.id,
        title: `#${o.id}`,
        customerName: customerNameOf(o),
        meta: formatDateTime(o.createdAt),
        price: this.renderTechBadges(o.items),
        badges: [],
        orderKey: typeof o.kanbanOrder === 'number' ? o.kanbanOrder : Date.parse(o.createdAt)
      }),
      onOpen: (id) => this.openDetail(id),
      onDrop: async (id, { status, statusChanged, orderKey }) => {
        if (statusChanged) {
          await this.setOrderStatus(id, status, 'Změna stavu přetažením v kanbanu výroby');
        }
        // Pořadí sdílí s objednávkami (stejná entita) – uloží ordersListManager helper
        if (window.ordersListManager) {
          await window.ordersListManager.saveKanbanOrder(id, orderKey);
        }
      }
    });
  }

  // Tichý refresh (auto-refresh / realtime push): obnoví jen seznam zakázek.
  // Pokud je otevřený detail zakázky, nic nedělá, aby uživateli nezmizel kontext.
  async refresh() {
    const container = document.getElementById('vyroba-section-content');
    if (!container || !document.getElementById('vyroba-list')) return;
    try {
      await this.load();
    } catch (_) {
      // tichý refresh – chybu neukazujeme, další pokus proběhne v dalším intervalu
    }
  }

  renderError(message) {
    const container = document.getElementById('vyroba-section-content');
    if (!container) return;
    container.innerHTML = renderErrorState(message, 'window.vyrobaManager.load()');
  }

  /** Badge technologií položek (OFSET/DIGI) – z list projekce (item.technologie). */
  renderTechBadges(items) {
    const badge = (tech) => tech === 'ofset'
      ? '<span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/50">OFSET</span>'
      : tech === 'digital'
        ? '<span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200/50">DIGI</span>'
        : '<span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-50 text-gray-700 border border-gray-200/50">AUTO</span>';
    const techs = (items || []).map((it) => it.technologie || (it.calculationResult && it.calculationResult.tiskTechnologie) || 'auto');
    return techs.map(badge).join(' ');
  }

  renderOrderCard(o) {
    const items = o.items || [];
    const itemsDesc = items.map((it) => `
      <span class="inline-flex items-center gap-1 mr-2">
        <span class="font-medium text-gray-700">${escapeHtml(it.name)}</span>
        ${this.renderTechBadges([it])}
      </span>
    `).join('');

    return renderListCard({
      id: o.id,
      title: `Zakázka <span class="font-semibold">#${escapeHtml(o.id)}</span>`,
      status: { kind: 'vyroba', value: o.status || 'received' },
      customerName: customerNameOf(o),
      metaLine: `
        <span class="inline-flex items-center">${icon('calendar', 'w-3.5 h-3.5 mr-1 text-gray-400')}${formatDateTime(o.createdAt)}</span>
        <span>·</span><span>${o.offerId ? `z nabídky #${escapeHtml(o.offerId)}` : 'přímá zakázka'}</span>
        ${o.createdByName ? `<span>·</span><span>${escapeHtml(o.createdByName)}</span>` : ''}
      `,
      description: itemsDesc,
      price: '',
      compact: this.view.compact,
      actions: [
        { action: 'open', icon: 'clipboard-check', label: 'Otevřít technologický list', tone: 'primary' }
      ]
    });
  }

  _bindListEvents() {
    const list = this.view.listContainer;
    if (!list) return;
    list.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        if (actionBtn.dataset.action === 'open') this.openDetail(actionBtn.dataset.id);
        return;
      }
      const row = e.target.closest('[data-row-id]');
      if (row && !e.target.closest('button, a')) this.openDetail(row.dataset.rowId);
    });
  }

  // ---------- Detail zakázky (Technologická karta) ----------

  async openDetail(id) {
    const container = document.getElementById('vyroba-section-content');
    if (!container) return;

    // Detail se kreslí přes celou sekci – skořápka seznamu se obnoví při návratu.
    container.innerHTML = `
      <div class="flex justify-center p-8 text-gray-400">
        ${icon('spinner', 'w-8 h-8 animate-spin')}
      </div>
    `;

    try {
      const order = await this.getOrder(id);
      this.currentOrder = order;
      this.renderDetail(order);
    } catch (err) {
      this.renderError(err.message);
    }
  }

  /**
   * Vytáhne HTML schéma vyřazení (rozkres tiskových desek) z debugInfo položky.
   * debugInfo je uložené přímo na položce; rozkres je obalen <div data-rozkres-desek>
   * a sahá až na konec řetězce (generuje backend/calc/calc-output.js).
   */
  layoutHtmlFor(it) {
    const src = (it && it.debugInfo)
      || (it && it.calculationResult && it.calculationResult.debugInfo)
      || '';
    const idx = src.indexOf('<div data-rozkres-desek');
    return idx >= 0 ? src.slice(idx) : '';
  }

  /** Bezpečné řádky adresy (customerData může být string i objekt). */
  customerLines(o) {
    return customerDataText(o.customerData).split('\n');
  }

  renderDetail(o) {
    const container = document.getElementById('vyroba-section-content');
    if (!container) return;

    const meta = STATUS_META[o.status] || { label: o.status, badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400' };
    const created = new Date(o.createdAt).toLocaleString('cs-CZ');
    const customerText = customerDataText(o.customerData);
    const customerLines = this.customerLines(o);
    // Logistika z formData první položky (doprava/zabalení jsou per zakázka)
    const fd0 = (o.items && o.items[0] && o.items[0].formData) || {};

    container.innerHTML = `
      <div class="space-y-6">
        <!-- Ovládací lišta -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-200 pb-4">
          <div class="flex items-center gap-3">
            <button id="vyroba-back-btn" class="btn btn-secondary btn-sm inline-flex items-center">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              Zpět na zakázky
            </button>
            <h3 class="text-xl font-bold text-gray-900">Zakázka #${this.escape(o.id)}</h3>
          </div>
          <div class="flex items-center gap-2.5 flex-wrap">
            <span class="inline-flex items-center text-sm font-semibold px-3 py-1 rounded-full ${meta.badge}">
              <span class="w-1.5 h-1.5 rounded-full mr-2 ${meta.dot}"></span>${meta.label}
            </span>
            <select id="vyroba-detail-status" class="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-blue-500 focus:border-blue-500">
              <option value="received" ${o.status === 'received' ? 'selected' : ''}>Přijato / Příprava</option>
              <option value="in_progress" ${o.status === 'in_progress' ? 'selected' : ''}>Ve výrobě</option>
              <option value="done" ${o.status === 'done' ? 'selected' : ''}>Hotovo (k expedici)</option>
              <option value="delivered" ${o.status === 'delivered' ? 'selected' : ''}>Odesláno / Předáno</option>
              <option value="cancelled" ${o.status === 'cancelled' ? 'selected' : ''}>Zrušeno</option>
            </select>
            <button id="vyroba-print-tech-list" class="btn btn-primary btn-sm inline-flex items-center gap-1.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Tisk tech. listu (A4)
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Levý sloupec: Informace o zakázce, adresy, DPD -->
          <div class="space-y-6 lg:col-span-1">
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
              <h4 class="text-sm font-semibold text-gray-800 uppercase tracking-wider border-b border-gray-100 pb-2">Přehled zakázky</h4>
              <div class="text-sm space-y-2">
                <div class="flex justify-between"><span class="text-gray-500">Zákazník:</span><span class="font-medium text-gray-900">${this.escape(customerLines[0] || '—')}</span></div>
                <div class="flex justify-between"><span class="text-gray-500">Vytvořeno:</span><span class="text-gray-900">${created}</span></div>
                <div class="flex justify-between"><span class="text-gray-500">Vystavil:</span><span class="text-gray-900">${this.escape(o.createdByName || o.createdBy || 'system')}</span></div>
                <div class="flex justify-between"><span class="text-gray-500">Z nabídky:</span><span class="text-gray-900">${o.offerId ? `#${this.escape(o.offerId)}` : 'přímá zakázka'}</span></div>
                ${o.reprintOfId ? `<div class="flex justify-between"><span class="text-gray-500">Reprint zakázky:</span><span class="text-gray-900">#${this.escape(o.reprintOfId)}</span></div>` : ''}
                <div class="flex justify-between"><span class="text-gray-500">Počet položek:</span><span class="font-bold text-gray-900">${(o.items || []).length}</span></div>
                ${o.invoicedAsId ? `<div class="flex justify-between"><span class="text-gray-500">Faktura:</span><span class="text-gray-900">${this.escape(o.invoicedAsId)}</span></div>` : ''}
              </div>
              ${o.note ? `
                <div class="border-t border-amber-100 pt-3">
                  <div class="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">📝 Poznámka k zakázce</div>
                  <p class="text-sm text-gray-800 bg-amber-50 border border-amber-100 rounded-md p-2.5 whitespace-pre-wrap">${this.escape(o.note)}</p>
                </div>
              ` : ''}
            </div>

            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
              <h4 class="text-sm font-semibold text-gray-800 uppercase tracking-wider border-b border-gray-100 pb-2">Dodání a logistika</h4>
              <div class="text-sm space-y-2">
                <div class="flex justify-between"><span class="text-gray-500">Doprava:</span><span class="text-gray-900">${fd0.doprava && fd0.doprava !== '0' ? 'Kurýrní služba' : 'Osobní odběr / neurčeno'}</span></div>
                <div class="flex justify-between"><span class="text-gray-500">Zabalení:</span><span class="text-gray-900">${fd0.zabaleni && fd0.zabaleni !== '0' ? 'Ano' : 'Standardní'}</span></div>
                ${fd0.dodat ? `<div class="flex justify-between"><span class="text-gray-500">Dodat:</span><span class="text-gray-900">${this.escape(fd0.dodat)}</span></div>` : ''}
              </div>
              <pre class="text-sm text-gray-700 font-sans whitespace-pre-wrap leading-relaxed bg-gray-50/50 p-3 rounded-md border border-gray-100">${this.escape(fd0.adresa || customerText || 'Žádné doplňující adresní údaje')}</pre>
            </div>

            <!-- DPD Expediční lístek -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4">
              <div class="flex justify-between items-center border-b border-gray-100 pb-2">
                <h4 class="text-sm font-semibold text-gray-800 uppercase tracking-wider">DPD Přepravní štítek</h4>
                <span class="px-2 py-0.5 text-[9px] bg-red-100 text-red-700 font-bold rounded border border-red-200">EXPRES</span>
              </div>
              <p class="text-xs text-gray-500">Vygenerovaný přepravní štítek pro kurýra DPD k nalepení na krabici.</p>
              
              <div class="border border-gray-300 rounded p-4 bg-gray-50 flex flex-col items-center">
                <!-- DPD logo -->
                <div class="flex justify-between items-center w-full mb-3">
                  <span class="font-extrabold text-lg text-red-600 leading-none">dpd</span>
                  <span class="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Classic Service</span>
                </div>
                
                <!-- Adresa v labelu -->
                <div class="w-full text-[10px] text-gray-700 font-medium space-y-1 mb-3">
                  <div class="border-b border-gray-200 pb-1 font-bold">Příjemce:</div>
                  <div class="truncate font-bold">${this.escape(customerLines[0] || 'Neznámý')}</div>
                  <div class="truncate">${this.escape(customerLines[1] || '')}</div>
                  <div class="truncate">${this.escape(customerLines[2] || '')}</div>
                </div>

                <!-- Fake čárový kód pomocí divů -->
                <div class="flex items-end justify-center h-12 w-full bg-white border border-gray-200 p-1 mb-1">
                  <!-- Generujeme náhodné šířky čárek -->
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-1.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-1 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-2 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-1 h-full bg-black mx-[1px]"></div>
                  <div class="w-1.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-1.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-1 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                  <div class="w-2 h-full bg-black mx-[1px]"></div>
                  <div class="w-1 h-full bg-black mx-[1px]"></div>
                  <div class="w-0.5 h-full bg-black mx-[1px]"></div>
                </div>
                <div class="text-[9px] font-mono tracking-widest text-gray-600 mb-4">DPD-9471-${o.id}</div>

                <button id="vyroba-print-dpd" class="btn btn-secondary btn-sm w-full inline-flex items-center justify-center gap-1.5 bg-white">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                  </svg>
                  Vytisknout štítek (10x15)
                </button>
              </div>
            </div>
          </div>

          <!-- Pravý sloupec: Seznam položek a jejich technologické karty s rozkresy vyřazení -->
          <div class="lg:col-span-2 space-y-6">
            <h4 class="text-lg font-bold text-gray-800 tracking-tight flex items-center justify-between">
              <span>Technologické karty položek</span>
              <span class="text-xs font-normal text-gray-500">Celkem ${(o.items || []).length} položek</span>
            </h4>

            ${(o.items || []).map((it, idx) => this.renderItemTechCard(it, idx, o)).join('')}
          </div>
        </div>
      </div>
    `;

    // Eventy
    document.getElementById('vyroba-back-btn')?.addEventListener('click', () => this.load());
    document.getElementById('vyroba-print-tech-list')?.addEventListener('click', () => this.printTechnicalList(o));
    document.getElementById('vyroba-print-dpd')?.addEventListener('click', () => this.printDpdLabel(o));
    
    // Změna stavu zakázky
    document.getElementById('vyroba-detail-status')?.addEventListener('change', async (e) => {
      const newStatus = e.target.value;
      try {
        const updated = await this.setOrderStatus(o.id, newStatus, 'Změna stavu v modulu Výroba');
        showNotification(`Stav zakázky byl úspěšně změněn na "${STATUS_META[newStatus]?.label || newStatus}"`, 'success');
        this.renderDetail(updated);
        // Synchronizovat s orders listem pokud existuje
        if (window.ordersListManager) {
          window.ordersListManager.fetchOrders();
        }
      } catch (err) {
        showNotification(err.message, 'error');
        e.target.value = o.status; // vrátit zpět
      }
    });
  }

  // HTML rozpis motivů (jména + počty kusů) pro tech kartu a tiskové listy.
  renderMotivyTable(motivy, compact = false) {
    if (!Array.isArray(motivy) || motivy.length < 2) return '';
    const celkem = motivy.reduce((a, m) => a + (+m.pocetKusu || 0), 0);
    const rows = motivy.map((m, i) => `
      <tr>
        <td style="padding:3px 10px 3px 0; color:#64748b;">${i + 1}.</td>
        <td style="padding:3px 10px 3px 0; font-weight:600;">${this.escape(m.nazev || 'Motiv ' + (i + 1))}</td>
        <td style="padding:3px 0; text-align:right; font-weight:700;">${m.pocetKusu} ks</td>
      </tr>`).join('');
    return `
      <div style="${compact ? 'margin-top:8px;' : 'margin-top:12px;'}">
        <div style="font-weight:800; font-size:${compact ? '11px' : '12px'}; text-transform:uppercase; color:#334155; margin-bottom:4px;">Motivy (${motivy.length})</div>
        <table style="font-size:${compact ? '12px' : '13px'}; border-collapse:collapse;">
          ${rows}
          <tr><td></td><td style="padding:4px 10px 0 0; border-top:1px solid #cbd5e1; color:#64748b;">celkem</td><td style="padding:4px 0 0; border-top:1px solid #cbd5e1; text-align:right; font-weight:800;">${celkem} ks</td></tr>
        </table>
      </div>`;
  }

  renderItemTechCard(it, idx, order) {
    const r = it.calculationResult || {};
    const tech = r.tiskTechnologie || 'auto';
    const hasVazba = r.vazba && r.vazba.typ !== '0' && r.vazba.nazev;
    const hasJinaObalka = hasVazba && r.vazba.jinaObalka && r.vazba.vnitrniBlok && !r.vazba.obalkaSloucena;

    let techDetailsHtml = '';

    if (hasJinaObalka && r.vazba.obalka && r.vazba.blok) {
      techDetailsHtml = `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <!-- Obálka -->
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div class="flex justify-between items-center border-b border-gray-200 pb-1.5">
              <span class="font-bold text-xs text-gray-800 uppercase tracking-wide">📘 Obálka</span>
              <span class="px-2 py-0.5 text-[9px] font-bold bg-blue-50 text-blue-700 rounded border border-blue-200/50">${r.vazba.obalka.tiskTechnologie.toUpperCase()}</span>
            </div>
            <div class="text-xs text-gray-600 space-y-1.5">
              <div><span class="text-gray-400">Gramáž a papír:</span> <strong class="text-gray-800">${this.escape(r.vazba.obalka.material?.nazev || '—')} (${r.vazba.obalka.material?.gramaz || '—'}g)</strong></div>
              <div><span class="text-gray-400">Barevnost:</span> <strong class="text-gray-800">${this.escape(r.vazba.obalka.barevnost || '—')}</strong></div>
              <div><span class="text-gray-400">Laminace:</span> <strong class="text-gray-800">${r.vazba.obalka.laminace && r.vazba.obalka.laminace !== '0' ? 'ANO' : 'NE'}</strong></div>
              <div><span class="text-gray-400">Počet archů k potisku:</span> <strong class="text-gray-800">${r.vazba.obalka.pocetArchu || 0} ks</strong></div>
              <div><span class="text-gray-400">Vyřazení na archu:</span> <strong class="text-indigo-700">${r.vazba.obalka.comp?.count || 0}x na arch</strong></div>
              <div><span class="text-gray-400">Tiskové desky (ofset):</span> <strong class="text-amber-700">${r.vazba.obalka.comp ? (r.vazba.obalka.comp.plates ? r.vazba.obalka.comp.plates.length : (r.vazba.obalka.comp.numberOfDifferentSheets + (r.vazba.obalka.comp.rewind ? 1 : 0))) : 0} sad</strong></div>
            </div>
          </div>

          <!-- Vnitřní blok -->
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div class="flex justify-between items-center border-b border-gray-200 pb-1.5">
              <span class="font-bold text-xs text-gray-800 uppercase tracking-wide">📖 Vnitřní blok</span>
              <span class="px-2 py-0.5 text-[9px] font-bold bg-amber-50 text-amber-700 rounded border border-amber-200/50">${r.vazba.blok.tiskTechnologie.toUpperCase()}</span>
            </div>
            <div class="text-xs text-gray-600 space-y-1.5">
              <div><span class="text-gray-400">Gramáž a papír:</span> <strong class="text-gray-800">${this.escape(r.vazba.blok.material?.nazev || '—')} (${r.vazba.blok.material?.gramaz || '—'}g)</strong></div>
              <div><span class="text-gray-400">Barevnost:</span> <strong class="text-gray-800">${this.escape(r.vazba.blok.barevnost || '—')}</strong></div>
              <div><span class="text-gray-400">Laminace:</span> <strong class="text-gray-800">${r.vazba.blok.laminace && r.vazba.blok.laminace !== '0' ? 'ANO' : 'NE'}</strong></div>
              <div><span class="text-gray-400">Počet archů k potisku:</span> <strong class="text-gray-800">${r.vazba.blok.pocetArchu || 0} ks</strong></div>
              <div><span class="text-gray-400">Vyřazení na archu:</span> <strong class="text-indigo-700">${r.vazba.blok.comp?.count || 0}x na arch</strong></div>
              <div><span class="text-gray-400">Tiskové desky (ofset):</span> <strong class="text-amber-700">${r.vazba.blok.comp ? (r.vazba.blok.comp.plates ? r.vazba.blok.comp.plates.length : (r.vazba.blok.comp.numberOfDifferentSheets + (r.vazba.blok.comp.rewind ? 1 : 0))) : 0} sad</strong></div>
            </div>
          </div>
        </div>
      `;
    } else {
      // Jednodílná zakázka (volné listy nebo sloučená obálka).
      // Primárně strukturovaný výsledek kalkulace (r), fallback formData –
      // starší zakázky calculationResult uložený nemají.
      const fd = it.formData || {};
      const deskyPocet = r.comp ? (r.comp.plates ? r.comp.plates.length : (r.comp.numberOfDifferentSheets + (r.comp.rewind ? 1 : 0) + (r.comp.restSheets > 0 ? 1 : 0))) : null;
      const naklad = r.pocetKusu || Number(fd.pocetKusu) || 0;
      const rozmer = `${fd.produktRozmerSirka || fd.rozmerSirka || '—'}×${fd.produktRozmerVyska || fd.rozmerVyska || '—'} mm`;
      const format = fd.produktRozmerFormat && fd.produktRozmerFormat !== 'vlastni' ? ` (${this.escape(fd.produktRozmerFormat)})` : '';
      const barevnost = fd.pocetBarev || fd.barevnost || '—';
      const papir = fd.materialName || fd.material || '—';
      techDetailsHtml = `
        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4 space-y-2.5 text-xs text-gray-600 max-w-full">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
            <div><span class="text-gray-400">Papír:</span> <strong class="text-gray-800">${this.escape(papir)}</strong></div>
            <div><span class="text-gray-400">Formát produktu:</span> <strong class="text-gray-800">${rozmer}${format}</strong></div>
            <div><span class="text-gray-400">Barevnost:</span> <strong class="text-gray-800">${this.escape(barevnost)}</strong></div>
            <div><span class="text-gray-400">Vyřazení na archu:</span> <strong class="text-indigo-700">${r.comp?.count ? `${r.comp.count}x ${r.orientace || 'na výšku'}` : '—'}</strong></div>
            <div><span class="text-gray-400">Spotřeba archů:</span> <strong class="text-gray-800">${r.pocetArchu ? `${r.pocetArchu} ks ${r.tiskFormat || 'SRA3'}` : '—'}</strong></div>
            <div><span class="text-gray-400">Technologie:</span> <strong class="text-gray-800 uppercase">${tech === 'digital' ? 'Digitální tisk' : tech === 'ofset' ? 'Ofsetový tisk' : 'Auto (dle kalkulace)'}</strong></div>
            ${tech === 'ofset' && deskyPocet != null ? `<div><span class="text-gray-400">Tiskové desky:</span> <strong class="text-amber-700">${deskyPocet} sad desek</strong></div>` : ''}
            <div><span class="text-gray-400">Počet druhů:</span> <strong class="text-gray-800">${r.pocetDruhu || fd.pocetDruhu || 1} druh(ů)</strong></div>
            <div><span class="text-gray-400">Celkový náklad:</span> <strong class="text-gray-800 font-bold">${naklad.toLocaleString('cs-CZ')} ks</strong></div>
            ${fd.primeBarvy && fd.primeBarvy !== '0' ? `<div><span class="text-gray-400">Přímé barvy:</span> <strong class="text-gray-800">${this.escape(fd.primeBarvy)}</strong></div>` : ''}
            ${fd.dtp && fd.dtp !== '0' ? `<div><span class="text-gray-400">DTP / grafika:</span> <strong class="text-gray-800">Ano</strong></div>` : ''}
            ${fd.typCeny ? `<div><span class="text-gray-400">Cenová úroveň:</span> <strong class="text-gray-800">${fd.typCeny === 'malo' ? 'maloobchod' : 'velkoobchod'}</strong></div>` : ''}
          </div>
          ${r.vazba?.obalkaSloucena ? `
            <div class="border-t border-emerald-100 pt-2 text-[10px] text-emerald-700 font-semibold flex items-center gap-1">
              <span>ℹ️ Obálka i vnitřek jsou identické a byly sloučeny do jednoho vyřazení (tisk z jedné formy).</span>
            </div>
          ` : ''}
        </div>
      `;
    }

    // Knihařská dokončení
    const finishingOperations = [];
    if (it.formData?.laminace && it.formData.laminace !== '0') finishingOperations.push(`Laminace (kód: ${it.formData.laminace})`);
    if (it.formData?.lakovani && it.formData.lakovani !== '0') finishingOperations.push(`Lakování (kód: ${it.formData.lakovani})`);
    if (it.formData?.falcovani && it.formData.falcovani !== '0') finishingOperations.push(`Falcování (lomy: ${it.formData.falcovani})`);
    if (it.formData?.bigovani && it.formData.bigovani !== '0') finishingOperations.push(`Bigování (bígů: ${it.formData.bigovani})`);
    if (it.formData?.vysek && it.formData.vysek !== '0') finishingOperations.push(`Výsek (raznice${it.formData.pozicNaVysek ? `, pozic: ${it.formData.pozicNaVysek}` : ''})`);
    if (it.formData?.predsek && it.formData.predsek !== '0') finishingOperations.push(`Předsek${it.formData.pozicNaPredsek ? ` (pozic: ${it.formData.pozicNaPredsek})` : ''}`);
    if (it.formData?.razba && it.formData.razba !== '0') finishingOperations.push(`Ražba${it.formData.pozicNaRazbu ? ` (pozic: ${it.formData.pozicNaRazbu})` : ''}`);
    if (it.formData?.vrtani && it.formData.vrtani !== '0') finishingOperations.push(`Vrtání (otvorů: ${it.formData.vrtani})`);
    if (it.formData?.perforace && it.formData.perforace !== '0') finishingOperations.push(`Perforace`);
    if (it.formData?.lepeni && it.formData.lepeni !== '0') finishingOperations.push(`Lepení${it.formData.lepeniPocet ? ` (${it.formData.lepeniPocet}×)` : ''}`);
    if (it.formData?.personalizace && it.formData.personalizace !== '0') finishingOperations.push(`Personalizace${it.formData.personalizacePocet ? ` (${it.formData.personalizacePocet} záznamů)` : ''}`);
    if (it.formData?.vazba && it.formData.vazba !== '0') finishingOperations.push(`Vazba: ${r.vazba?.nazev || 'ANO'}${r.vazba?.barvaSpiralyText ? ` – spirála ${r.vazba.barvaSpiralyText}` : ''}`);
    if (r.vazba?.cenaPodlozka > 0) finishingOperations.push(`Podložka: ${r.vazba.materialPodlozka || 'šedá lepenka'}`);

    const finishingHtml = finishingOperations.length > 0
      ? `
        <div class="mt-4">
          <div class="text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">🔧 Knihařské dokončovací operace:</div>
          <div class="flex flex-wrap gap-2">
            ${finishingOperations.map(op => `<span class="px-2.5 py-1 text-xs bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-md font-semibold">${op}</span>`).join('')}
          </div>
        </div>
      `
      : '';

    // Schéma vyřazení tiskových desek – HTML render z kalkulačního debugInfo
    // (uloženo přímo na položce, wrapper <div data-rozkres-desek>).
    const layoutVisualHtml = this.layoutHtmlFor(it);

    return `
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <div class="flex justify-between items-start border-b border-gray-100 pb-3 flex-wrap gap-2">
          <div>
            <h5 class="text-md font-bold text-gray-900">${idx + 1}. ${this.escape(it.name)}</h5>
            <p class="text-xs text-gray-500 mt-1">Položka zakázky #${this.escape(order.id)}</p>
          </div>
          <div class="flex items-center gap-2">
            <button onclick="window.vyrobaManager.printAccompanyingSheet('${order.id}', ${idx})" class="btn btn-secondary btn-xs py-1 px-2.5 text-xs text-indigo-700 bg-indigo-50 border-indigo-100 hover:bg-indigo-100 inline-flex items-center gap-1">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Tisk průvodky (A4)
            </button>
          </div>
        </div>

        ${techDetailsHtml}
        ${rozsahProVyrobu(r.vazba) ? `
          <div class="text-xs text-gray-600 mt-3"><span class="font-bold text-gray-700 uppercase tracking-wide">📐 Rozsah:</span> <strong class="text-gray-800">${rozsahProVyrobu(r.vazba)}</strong></div>
        ` : ''}
        ${this.renderMotivyTable(r.motivy)}
        ${finishingHtml}

        <!-- Vizualizace vyřazení -->
        ${layoutVisualHtml ? `
          <div class="border-t border-gray-100 pt-4 max-w-full overflow-hidden">
            <div class="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">📍 Schéma vyřazení na tiskovou desku:</div>
            <div class="bg-gray-50/50 rounded-lg p-3 border border-gray-100 flex justify-center">
              ${layoutVisualHtml}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  // ---------- Tiskové šablony (Technologický list, Průvodní list, DPD) ----------

  printTechnicalList(order) {
    const printWindow = window.open('', '_blank');
    const created = new Date(order.createdAt).toLocaleString('cs-CZ');
    
    let itemsHtml = '';
    (order.items || []).forEach((it, idx) => {
      const r = it.calculationResult || {};
      const tech = r.tiskTechnologie || 'auto';
      const isOffset = tech === 'ofset';
      const hasVazba = r.vazba && r.vazba.typ !== '0' && r.vazba.nazev;
      const hasJinaObalka = hasVazba && r.vazba.jinaObalka && r.vazba.vnitrniBlok && !r.vazba.obalkaSloucena;
      const layoutVisualHtml = this.layoutHtmlFor(it);
      
      let specHtml = '';
      if (hasJinaObalka && r.vazba.obalka && r.vazba.blok) {
        specHtml = `
          <div class="tech-grid">
            <div class="tech-box">
              <div class="tech-box-title">📘 Obálka - ${r.vazba.obalka.tiskTechnologie.toUpperCase()}</div>
              <table>
                <tr><td>Papír:</td><td><strong>${r.vazba.obalka.material?.nazev} (${r.vazba.obalka.material?.gramaz}g)</strong></td></tr>
                <tr><td>Barevnost:</td><td><strong>${r.vazba.obalka.barevnost}</strong></td></tr>
                <tr><td>Počet archů:</td><td><strong>${r.vazba.obalka.pocetArchu} ks</strong></td></tr>
                <tr><td>Vyřazení:</td><td><strong>${r.vazba.obalka.comp?.count}x na arch</strong></td></tr>
              </table>
            </div>
            <div class="tech-box">
              <div class="tech-box-title">📖 Vnitřní blok - ${r.vazba.blok.tiskTechnologie.toUpperCase()}</div>
              <table>
                <tr><td>Papír:</td><td><strong>${r.vazba.blok.material?.nazev} (${r.vazba.blok.material?.gramaz}g)</strong></td></tr>
                <tr><td>Barevnost:</td><td><strong>${r.vazba.blok.barevnost}</strong></td></tr>
                <tr><td>Počet archů:</td><td><strong>${r.vazba.blok.pocetArchu} ks</strong></td></tr>
                <tr><td>Vyřazení:</td><td><strong>${r.vazba.blok.comp?.count}x na arch</strong></td></tr>
              </table>
            </div>
          </div>
        `;
      } else {
        const fd = it.formData || {};
        const deskyPocet = r.comp ? (r.comp.plates ? r.comp.plates.length : (r.comp.numberOfDifferentSheets + (r.comp.rewind ? 1 : 0) + (r.comp.restSheets > 0 ? 1 : 0))) : null;
        const naklad = r.pocetKusu || Number(fd.pocetKusu) || 0;
        specHtml = `
          <table class="simple-spec-table">
            <tr>
              <td><strong>Náklad:</strong> ${naklad.toLocaleString('cs-CZ')} ks</td>
              <td><strong>Rozměr:</strong> ${fd.produktRozmerSirka || fd.rozmerSirka || '—'}x${fd.produktRozmerVyska || fd.rozmerVyska || '—'} mm</td>
              <td><strong>Papír:</strong> ${fd.materialName || fd.material || '—'}</td>
            </tr>
            <tr>
              <td><strong>Barevnost:</strong> ${fd.pocetBarev || fd.barevnost || '—'}</td>
              <td><strong>Technologie:</strong> ${tech.toUpperCase()}</td>
              <td><strong>Archy:</strong> ${r.pocetArchu ? `${r.pocetArchu} ks (${r.tiskFormat || 'SRA3'})` : '—'}</td>
            </tr>
            <tr>
              <td><strong>Vyřazení:</strong> ${r.comp?.count ? `${r.comp.count}x na archu` : '—'}</td>
              <td><strong>Počet druhů:</strong> ${r.pocetDruhu || fd.pocetDruhu || 1} motiv(ů)</td>
              <td><strong>Tiskové desky:</strong> ${isOffset ? (deskyPocet != null ? `${deskyPocet} sad` : '—') : 'nevyžaduje'}</td>
            </tr>
          </table>
        `;
      }

      // Knihařská dokončení
      const finishingList = [];
      if (it.formData?.laminace && it.formData.laminace !== '0') finishingList.push(`Laminace (${it.formData.laminace})`);
      if (it.formData?.lakovani && it.formData.lakovani !== '0') finishingList.push(`Lakování (${it.formData.lakovani})`);
      if (it.formData?.falcovani && it.formData.falcovani !== '0') finishingList.push(`Falcování (${it.formData.falcovani} lomy)`);
      if (it.formData?.bigovani && it.formData.bigovani !== '0') finishingList.push(`Bigování (${it.formData.bigovani} rýhy)`);
      if (it.formData?.vysek && it.formData.vysek !== '0') finishingList.push(`Výsek`);
      if (it.formData?.predsek && it.formData.predsek !== '0') finishingList.push(`Předsek`);
      if (it.formData?.razba && it.formData.razba !== '0') finishingList.push(`Ražba`);
      if (it.formData?.vrtani && it.formData.vrtani !== '0') finishingList.push(`Vrtání (${it.formData.vrtani} otvory)`);
      if (it.formData?.perforace && it.formData.perforace !== '0') finishingList.push(`Perforace`);
      if (it.formData?.lepeni && it.formData.lepeni !== '0') finishingList.push(`Lepení`);
      if (it.formData?.personalizace && it.formData.personalizace !== '0') finishingList.push(`Personalizace`);
      if (it.formData?.vazba && it.formData.vazba !== '0') finishingList.push(`Vazba: ${r.vazba?.nazev || 'ANO'}${r.vazba?.barvaSpiralyText ? ` – spirála ${r.vazba.barvaSpiralyText}` : ''}`);
      if (r.vazba?.cenaPodlozka > 0) finishingList.push(`Podložka: ${r.vazba.materialPodlozka || 'šedá lepenka'}`);

      const finishingHtml = finishingList.length > 0
        ? `<div class="finishing-section"><strong>Knihařské dokončení:</strong> ${finishingList.join(' — ')}</div>`
        : '';

      const rozsahHtml = rozsahProVyrobu(r.vazba)
        ? `<div style="font-size:12px; margin-top:6px;"><strong>Rozsah:</strong> ${rozsahProVyrobu(r.vazba)}</div>`
        : '';
      itemsHtml += `
        <div class="item-card">
          <div class="item-card-header">${idx + 1}. ${it.name}</div>
          ${specHtml}
          ${rozsahHtml}
          ${this.renderMotivyTable(r.motivy, true)}
          ${finishingHtml}
          ${layoutVisualHtml ? `<div class="layout-visual-container">${layoutVisualHtml}</div>` : ''}
        </div>
      `;
    });

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Technologický list #${order.id}</title>
        <meta charset="utf-8" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap" rel="stylesheet">
        <style>
          body { font-family: 'Inter', sans-serif; color: #1e293b; line-height: 1.5; margin: 0; padding: 25px; font-size: 13px; }
          .header { display: flex; justify-content: space-between; align-items: start; border-b: 2px solid #e2e8f0; padding-bottom: 15px; margin-bottom: 20px; }
          .logo { font-size: 24px; font-weight: 800; color: #1d4ed8; text-transform: uppercase; letter-spacing: 1px; }
          .order-title { font-size: 20px; font-weight: 700; text-align: right; margin: 0; color: #0f172a; }
          .meta-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .meta-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; background-color: #f8fafc; }
          .meta-box h4 { margin: 0 0 8px 0; font-size: 11px; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: 0.5px; }
          .meta-box pre { font-family: inherit; font-size: 12px; margin: 0; white-space: pre-wrap; }
          .item-card { border: 1px solid #cbd5e1; border-radius: 8px; padding: 15px; margin-bottom: 20px; page-break-inside: avoid; }
          .item-card-header { font-size: 14px; font-weight: 700; color: #1e3a8a; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 12px; }
          .tech-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 15px; margin-bottom: 12px; }
          .tech-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; background-color: #f8fafc; }
          .tech-box-title { font-size: 11px; font-weight: 700; color: #334155; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 8px; }
          .tech-box table, .simple-spec-table { width: 100%; border-collapse: collapse; font-size: 12px; }
          .tech-box td { padding: 3px 0; vertical-align: top; }
          .simple-spec-table td { padding: 6px 0; border-bottom: 1px solid #f1f5f9; width: 33.33%; }
          .finishing-section { margin-top: 10px; background-color: #eff6ff; border: 1px solid #bfdbfe; padding: 8px 12px; border-radius: 6px; font-size: 11.5px; color: #1e40af; }
          .layout-visual-container { margin-top: 15px; border-top: 1px dashed #e2e8f0; padding-top: 12px; display: flex; justify-content: center; }
          .signature-section { display: flex; justify-content: space-between; margin-top: 40px; page-break-inside: avoid; }
          .sig-box { width: 200px; border-top: 1px solid #94a3b8; text-align: center; padding-top: 6px; font-size: 11px; color: #64748b; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
          /* Styly pro vizualizaci z calc-output.js */
          .bg-gray-50 { background-color: #f9fafb !important; }
          .border { border: 1px solid #e5e7eb !important; }
          .rounded-lg { border-radius: 0.5rem !important; }
          .p-3 { padding: 0.75rem !important; }
          .mb-3 { margin-bottom: 0.75rem !important; }
          .text-xs { font-size: 0.75rem !important; }
          .font-bold { font-weight: 700 !important; }
          .text-gray-700 { color: #374151 !important; }
          .text-\[10px\] { font-size: 10px !important; }
          .text-gray-400 { color: #9ca3af !important; }
          .mb-2 { margin-bottom: 0.5rem !important; }
          .relative { position: relative !important; }
          .bg-white { background-color: #ffffff !important; }
          .border-gray-400 { border-color: #9ca3af !important; }
          .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important; }
          .mx-auto { margin-left: auto !important; margin-right: auto !important; }
          .absolute { position: absolute !important; }
          .border-white\/60 { border-color: rgba(255, 255, 255, 0.6) !important; }
          .text-\[9px\] { font-size: 9px !important; }
          .text-\[11px\] { font-size: 11px !important; }
          .text-white { color: #ffffff !important; }
          .flex { display: flex !important; }
          .flex-col { flex-direction: column !important; }
          .items-center { align-items: center !important; }
          .justify-center { justify-content: center !important; }
          .justify-between { justify-content: space-between !important; }
          .gap-1 { gap: 0.25rem !important; }
          .mt-2 { margin-top: 0.5rem !important; }
          .mb-3 { margin-bottom: 0.75rem !important; }
          .px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
          .py-0\.5 { padding-top: 0.125rem !important; padding-bottom: 0.125rem !important; }
          .rounded-full { border-radius: 9999px !important; }
          .font-semibold { font-weight: 600 !important; }
          .uppercase { text-transform: uppercase !important; }
          .tracking-wider { letter-spacing: 0.05em !important; }
          .bg-indigo-100 { background-color: #e0e7ff !important; }
          .text-indigo-700 { color: #4338ca !important; }
          .text-indigo-800 { color: #3730a3 !important; }
          .border-gray-200 { border-color: #e5e7eb !important; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">TRIA tiskárna</div>
            <div style="font-size: 11px; color: #64748b; margin-top: 4px;">Výrobní a tiskové oddělení</div>
          </div>
          <div>
            <h1 class="order-title">TECHNOLOGICKÝ LIST</h1>
            <div style="font-size: 14px; font-weight: 700; text-align: right; color: #1d4ed8; margin-top: 3px;">Zakázka #${order.id}</div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-box">
            <h4>Odběratel a doručení</h4>
            <pre>${this.escape(customerDataText(order.customerData))}</pre>
          </div>
          <div class="meta-box">
            <h4>Informace o výrobě</h4>
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <tr><td style="padding:4px 0; color:#64748b;">Datum vystavení:</td><td><strong>${created}</strong></td></tr>
              <tr><td style="padding:4px 0; color:#64748b;">Vystavil:</td><td><strong>${this.escape(order.createdByName || order.createdBy || 'system')}</strong></td></tr>
              <tr><td style="padding:4px 0; color:#64748b;">Zdroj:</td><td><strong>${order.offerId ? `nabídka #${this.escape(order.offerId)}` : 'přímá zakázka'}</strong></td></tr>
              <tr><td style="padding:4px 0; color:#64748b;">Stav výroby:</td><td><strong style="color:#d97706; text-transform:uppercase;">${STATUS_META[order.status]?.label || order.status}</strong></td></tr>
              <tr><td style="padding:4px 0; color:#64748b;">Způsob dodání:</td><td><strong>Kurýrní služba (DPD Classic)</strong></td></tr>
            </table>
            ${order.note ? `<div style="margin-top:8px; border:1px solid #fcd34d; background:#fffbeb; border-radius:6px; padding:8px 10px; font-size:12px;"><strong>Poznámka:</strong> ${this.escape(order.note)}</div>` : ''}
          </div>
        </div>

        ${itemsHtml}

        <div class="signature-section">
          <div class="sig-box">Vydal (operátor výroby)</div>
          <div class="sig-box">Převzal (tiskař / CTP)</div>
          <div class="sig-box">Zkontroloval (dokončovací zpracování)</div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
          }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  }

  async printAccompanyingSheet(orderId, itemIndex) {
    // Plný objekt zakázky drží currentOrder (detail); jinak dotáhneme z API –
    // seznam má jen ořezanou projekci bez formData/calculationResult.
    let order = this.currentOrder && this.currentOrder.id === orderId ? this.currentOrder : null;
    if (!order) {
      try {
        order = await this.getOrder(orderId);
      } catch {
        return;
      }
    }
    const it = order.items[itemIndex];
    if (!it) return;

    const printWindow = window.open('', '_blank');
    const r = it.calculationResult || {};
    const finishingList = [];
    if (it.formData?.laminace && it.formData.laminace !== '0') finishingList.push(`LAMINACE: ${it.formData.laminace}`);
    if (it.formData?.lakovani && it.formData.lakovani !== '0') finishingList.push(`LAKOVÁNÍ: ${it.formData.lakovani}`);
    if (it.formData?.falcovani && it.formData.falcovani !== '0') finishingList.push(`FALCOVÁNÍ (${it.formData.falcovani}x lom)`);
    if (it.formData?.bigovani && it.formData.bigovani !== '0') finishingList.push(`BÍGOVÁNÍ (${it.formData.bigovani}x rýha)`);
    if (it.formData?.vysek && it.formData.vysek !== '0') finishingList.push(`VÝSEK (RAZNICE)`);
    if (it.formData?.vrtani && it.formData.vrtani !== '0') finishingList.push(`VRTÁNÍ (${it.formData.vrtani}x otvor)`);
    if (it.formData?.perforace && it.formData.perforace !== '0') finishingList.push(`PERFORACE`);
    if (it.formData?.vazba && it.formData.vazba !== '0') finishingList.push(`VAZBA: ${r.vazba?.nazev || 'ANO'}${r.vazba?.barvaSpiralyText ? ` – SPIRÁLA ${r.vazba.barvaSpiralyText.toUpperCase()}` : ''}`);
    if (r.vazba?.cenaPodlozka > 0) finishingList.push(`PODLOŽKA: ${(r.vazba.materialPodlozka || 'šedá lepenka').toUpperCase()}`);

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Průvodní list zakázky #${order.id}</title>
        <meta charset="utf-8" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800;900&display=swap" rel="stylesheet">
        <style>
          body { font-family: 'Inter', sans-serif; color: #000; margin: 0; padding: 40px; }
          .border-wrapper { border: 15px solid #1e3a8a; border-radius: 12px; padding: 40px; height: calc(100vh - 110px); display: flex; flex-direction: column; justify-content: space-between; box-sizing: border-box; }
          .header { display: flex; justify-content: space-between; align-items: start; border-bottom: 5px solid #000; padding-bottom: 20px; }
          .logo { font-size: 28px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; color: #1e3a8a; }
          .sheet-type { font-size: 16px; font-weight: 700; color: #ef4444; border: 2px solid #ef4444; padding: 4px 10px; border-radius: 4px; text-transform: uppercase; }
          .order-id { font-size: 72px; font-weight: 900; text-align: center; margin: 40px 0 20px 0; color: #1e3a8a; }
          .product-name { font-size: 32px; font-weight: 800; text-align: center; margin: 0 0 40px 0; text-transform: uppercase; border-bottom: 3px solid #e2e8f0; padding-bottom: 15px; }
          .specs-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 30px; font-size: 18px; margin-bottom: 30px; }
          .spec-item { border-bottom: 1px solid #cbd5e1; padding-bottom: 8px; }
          .spec-label { color: #64748b; font-size: 14px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
          .spec-value { font-weight: 700; font-size: 22px; }
          .finishing-checklist { border: 4px solid #1e3a8a; border-radius: 8px; padding: 20px; background-color: #f0f4f8; margin-top: 20px; }
          .checklist-title { font-size: 16px; font-weight: 800; text-transform: uppercase; color: #1e3a8a; border-bottom: 2px solid #1e3a8a; padding-bottom: 8px; margin-bottom: 15px; }
          .checklist-grid { display: grid; grid-template-cols: 1fr 1fr; gap: 15px; }
          .checklist-item { display: flex; align-items: center; font-size: 16px; font-weight: 700; }
          .checkbox { width: 22px; height: 22px; border: 2px solid #000; margin-right: 12px; flex-shrink: 0; background-color: #fff; }
          .footer-info { display: flex; justify-content: space-between; font-size: 14px; color: #475569; font-weight: 700; border-top: 2px solid #000; padding-top: 15px; }
        </style>
      </head>
      <body>
        <div class="border-wrapper">
          <div>
            <div class="header">
              <div>
                <div class="logo">TRIA tiskárna</div>
              </div>
              <div class="sheet-type">Průvodní list zakázky</div>
            </div>

            <div class="order-id">#${order.id}</div>
            <div class="product-name">${it.name}</div>

            <div class="specs-grid">
              <div class="spec-item">
                <div class="spec-label">Náklad k tisku:</div>
                <div class="spec-value" style="color: #ef4444; font-size: 36px;">${(r.pocetKusu || Number(it.formData?.pocetKusu) || 0).toLocaleString('cs-CZ')} ks</div>
              </div>
              <div class="spec-item">
                <div class="spec-label">Papír:</div>
                <div class="spec-value">${it.formData?.materialName || it.formData?.material || '—'}</div>
              </div>
              <div class="spec-item">
                <div class="spec-label">Technologie tisku:</div>
                <div class="spec-value" style="text-transform: uppercase; color: #1d4ed8;">${(r.tiskTechnologie || 'AUTO').toUpperCase()} (${r.tiskFormat || 'SRA3'})</div>
              </div>
              <div class="spec-item">
                <div class="spec-label">Barevnost:</div>
                <div class="spec-value">${it.formData?.pocetBarev || it.formData?.barevnost || '—'}</div>
              </div>
              <div class="spec-item">
                <div class="spec-label">Rozměr produktu:</div>
                <div class="spec-value">${it.formData?.produktRozmerSirka || '—'}×${it.formData?.produktRozmerVyska || '—'} mm</div>
              </div>
              ${rozsahProVyrobu(r.vazba) ? `
              <div class="spec-item" style="grid-column: 1 / -1;">
                <div class="spec-label">Rozsah:</div>
                <div class="spec-value" style="font-size: 18px;">${rozsahProVyrobu(r.vazba)}</div>
              </div>
              ` : ''}
            </div>

            ${Array.isArray(r.motivy) && r.motivy.length > 1 ? `
              <div style="border: 3px solid #7c3aed; border-radius: 8px; padding: 14px 18px; background-color: #f5f3ff; margin-bottom: 24px;">
                <div style="font-size: 14px; font-weight: 800; text-transform: uppercase; color: #5b21b6; margin-bottom: 8px;">🎨 Motivy – rozdělení nákladu (${r.motivy.length}):</div>
                ${r.motivy.map((m, i) => `
                  <div style="display:flex; justify-content:space-between; font-size:18px; font-weight:700; border-bottom:1px solid #ddd6fe; padding:4px 0;">
                    <span>${i + 1}. ${this.escape(m.nazev || 'Motiv ' + (i + 1))}</span>
                    <span>${m.pocetKusu} ks</span>
                  </div>`).join('')}
              </div>
            ` : ''}

            ${finishingList.length > 0 ? `
              <div class="finishing-checklist">
                <div class="checklist-title">🔧 KNIHAŘSKÉ ZPRACOVÁNÍ (Zaškrtněte po dokončení):</div>
                <div class="checklist-grid">
                  ${finishingList.map(op => `
                    <div class="checklist-item">
                      <div class="checkbox"></div>
                      <div>${op}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            ` : `
              <div class="finishing-checklist" style="border-color: #22c55e; background-color: #f0fdf4;">
                <div class="checklist-title" style="color: #15803d; border-color: #15803d;">🔧 DOKONČOVACÍ PRÁCE:</div>
                <div style="font-size: 16px; font-weight: 700; color: #166534;">ČISTÝ OŘEZ: Pouze nařezat na čistý formát rozměru! Žádné další knihařské úpravy nejsou vyžadovány.</div>
              </div>
            `}
          </div>

          <div class="footer-info">
            <div>VYSTAVIL: ${order.createdBy || 'system'}</div>
            <div>DATUM VÝROBY: ${new Date(order.createdAt).toLocaleDateString('cs-CZ')}</div>
            <div>ČÁST: ${itemIndex + 1} / ${(order.items || []).length}</div>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
          }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  }

  printDpdLabel(order) {
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    const recipient = this.customerLines(order);

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>DPD Štítek #${order.id}</title>
        <meta charset="utf-8" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800;900&display=swap" rel="stylesheet">
        <style>
          body { font-family: 'Inter', sans-serif; color: #000; margin: 0; padding: 15px; background: #fff; width: 375px; height: 560px; box-sizing: border-box; }
          .ticket-border { border: 2px solid #000; width: 100%; height: 100%; padding: 15px; box-sizing: border-box; display: flex; flex-direction: column; justify-content: space-between; }
          .logo-row { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .dpd-logo { font-size: 32px; font-weight: 900; color: #e11d48; margin: 0; line-height: 1; }
          .service-type { font-size: 12px; font-weight: 800; text-transform: uppercase; background: #000; color: #fff; padding: 3px 6px; border-radius: 3px; }
          .address-section { display: grid; grid-template-cols: 1fr; gap: 10px; margin-top: 15px; font-size: 11px; }
          .addr-box { border-bottom: 1px dashed #cbd5e1; padding-bottom: 8px; }
          .addr-title { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 3px; }
          .addr-body { font-weight: 700; font-size: 12px; line-height: 1.4; }
          .bar-code-mockup { margin-top: 15px; border: 1px solid #94a3b8; background: #fff; padding: 6px; display: flex; flex-direction: column; items: center; }
          .barcode-bars { display: flex; items: end; justify-content: center; h-16 w-full; height: 60px; width: 100%; }
          .barcode-num { font-size: 10px; font-weight: 700; letter-spacing: 3px; margin-top: 4px; font-family: monospace; }
          .routing-info { display: flex; justify-content: space-between; border-top: 2px solid #000; padding-top: 10px; font-size: 10px; font-weight: 700; }
        </style>
      </head>
      <body>
        <div class="ticket-border">
          <div>
            <div class="logo-row">
              <span class="dpd-logo">dpd</span>
              <span class="service-type">DPD Classic</span>
            </div>

            <div class="address-section">
              <div class="addr-box">
                <div class="addr-title">Odesílatel:</div>
                <div class="addr-body" style="font-size: 11px; font-weight: 500;">
                  TRIA tiskárna s.r.o.<br>
                  Průmyslová 1052, 390 01 Tábor
                </div>
              </div>
              <div class="addr-box" style="border: none;">
                <div class="addr-title">Příjemce:</div>
                <div class="addr-body">
                  ${this.escape(recipient[0] || 'Neznámý příjemce')}<br>
                  ${this.escape(recipient[1] || '')}<br>
                  ${this.escape(recipient[2] || '')}<br>
                  ${this.escape(recipient[3] || '')}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="bar-code-mockup">
              <div class="barcode-bars">
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:2px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1.5 h-full bg-black mx-[1px]" style="width:4px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:2px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1 h-full bg-black mx-[1px]" style="width:3px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:1px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-2.5 h-full bg-black mx-[1px]" style="width:6px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1 h-full bg-black mx-[1px]" style="width:3px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:2px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1.5 h-full bg-black mx-[1px]" style="width:4px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:1px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-2 h-full bg-black mx-[1px]" style="width:5px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1 h-full bg-black mx-[1px]" style="width:3px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:1px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1.5 h-full bg-black mx-[1px]" style="width:4px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-1.5 h-full bg-black mx-[1px]" style="width:4px; height:100%; background:#000; margin:0 1px;"></div>
                <div class="w-0.5 h-full bg-black mx-[1px]" style="width:2px; height:100%; background:#000; margin:0 1px;"></div>
              </div>
              <div class="barcode-num">DPD-(020)9471${order.id}</div>
            </div>

            <div class="routing-info">
              <div>DEPOT: CZ-390</div>
              <div>VÁHA: 1.5 kg</div>
              <div>ID: #${order.id}</div>
            </div>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 300);
          }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  }

  // ---------- Pomocné funkce ----------

  escape(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
